<script setup lang="ts">
/**
 * 代管主機清單（fleet 首頁；operator 的落地頁）。
 *
 * - 排序由後端決定（crit → warn → 磁碟水位 → 名稱），前端**照順序渲染、不再自己排**。
 * - 15 秒輪詢（`lib/poll.ts`：切到背景暫停、同時只跑一個請求）。
 * - 「＋註冊主機」只有 admin 以上看得到（後端 `require_admin` 才是真閘門；這裡是 UX）。
 */
import { computed, onUnmounted, ref, watch } from 'vue'

import EnrollCodeCard from '@/components/EnrollCodeCard.vue'
import FilterClearButton from '@/components/FilterClearButton.vue'
import UiButton from '@/components/UiButton.vue'
import UiMeter from '@/components/UiMeter.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as hosting from '@/api/hosting'
import type { EnrollInfo, HostMount, ManagedHost, ManagedHostIn } from '@/api/hosting'
import { mountPath } from '@/api/hosting'
import { agentStaleLevel, fmtCapacity, hostStatusClass, hostStatusLabel } from '@/lib/fleet'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { usePolling } from '@/lib/poll'
import { fmtSeen } from '@/lib/staleness'
import { useHostingRoute } from '@/lib/hostingRoutes'
import { customerOptionLabel } from '@/lib/customerLabel'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

/**
 * 代管路由名的視角分流（批99-B）：我方員工＝`host-detail`，代理商＝`reseller-host-detail`。
 * 本頁兩個視角共用同一支元件，所以每個連結都得問一次自己現在是誰。
 */
const hr = useHostingRoute()

const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()

const hosts = ref<ManagedHost[]>([])
const loading = ref(true)
const loadError = ref('')
/**
 * 歸屬過濾（批99-B，**我方視角專用**）：`''`＝全部、`vendor`＝只看自營、其餘＝代理商 uuid。
 *
 * ★★ `vendor` **不送給後端**——`GET /hosts?reseller_id=` 只吃代理商 uuid，後端刻意沒有
 *   「只看自營」的值（查詢字串表達不了 `IS NULL` 與「沒帶」的差別），送 `vendor` 進去是
 *   FastAPI 的 422。所以自營那一檔在前端濾（`visibleHosts`；表 <50 列）。
 *
 * 代理商視角不畫這個下拉也不送這個參數——他看得到的本來就只有自家主機（後端一律加
 * `reseller_id == 我` 的條件，別家與我方自營主機對他是 404），多一個永遠只有一個選項的
 * 篩選器只是噪音。
 */
/** 只看自營（`reseller_id === null`）——前端濾的那一檔。 */
const VENDOR_ONLY = 'vendor'
// 批186：兩個過濾條件記住；要在 `usePolling` 之前（輪詢一註冊就立刻載入）。
const pf = usePersistedFilters('hosts', { includeRetired: false, resellerFilter: '' })
const { includeRetired, resellerFilter } = pf.refs
const { dirty: filtersDirty } = pf
/** 下面的 `watch` 會重載，清除只要改值。 */
const clearFilters = () => pf.clear()

function errText(e: unknown, fallback: string): string {
  const detail = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (detail == null) return fallback
  return typeof detail === 'string' ? detail : JSON.stringify(detail)
}

async function load() {
  try {
    const rid = resellerFilter.value
    hosts.value = await hosting.listManagedHosts({
      includeRetired: includeRetired.value,
      // 只有 uuid 才送得出去（`vendor` 那一檔在前端濾，見 `resellerFilter` 的註解）。
      resellerId: auth.isReseller || !rid || rid === VENDOR_ONLY ? undefined : rid,
    })
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取主機清單失敗')
  } finally {
    loading.value = false
  }
}

const poll = usePolling(load, 15_000)
watch([includeRetired, resellerFilter], () => void poll.refresh())

// 「支援產品」chip 要把產品鍵翻成中文（批99-A）。
// ⚠️ `GET /products` 是 admin 以上的端點——operator 會 403，`productName()` 就退回原始鍵
//    （`iplusone-cs`）。這比整格空白好，也不值得為它多開一支端點。
void store.ensureProducts().catch(() => {})
// 歸屬欄與過濾下拉的選項來源（批99-B）。⚠️ `GET /resellers` 是 **superadmin** 端點——
// admin／operator 會 403，`ensureResellers()` 自己吞掉並標記 `resellersDenied`，
// 這時退化成「用主機列自帶的 `reseller_name` 組選項」（見 `resellerOptions`）。
if (!auth.isReseller) void store.ensureResellers().catch(() => {})

/**
 * 過濾下拉的代理商選項。優先用 `store.resellers`（完整清單，含一台主機都還沒建的公司）；
 * 拿不到（admin 被 403）就從**已載入的主機**現推——少了沒有主機的公司，但篩得動看得到的。
 */
const resellerOptions = computed<{ id: string; name: string }[]>(() => {
  if (store.resellers.length) return store.resellers.map((r) => ({ id: r.id, name: r.name }))
  const seen = new Map<string, string>()
  for (const h of hosts.value) {
    if (h.reseller_id) seen.set(h.reseller_id, h.reseller_name || h.reseller_id.slice(0, 8))
  }
  return [...seen].map(([id, name]) => ({ id, name }))
})
/**
 * 過濾下拉實際列的選項（批186）：記住的代理商若不在 `resellerOptions`（公司已刪、或 admin 讀不到完整清單
 * 而該公司目前沒有主機），補一個占位選項，下拉才不會顯示空白但條件還在生效。建立對話框仍用 `resellerOptions`。
 */
const filterOptions = computed(() => {
  const cur = resellerFilter.value
  if (!cur || cur === VENDOR_ONLY || resellerOptions.value.some((r) => r.id === cur)) return resellerOptions.value
  return [...resellerOptions.value, { id: cur, name: '（目前篩選的代理商）' }]
})
/** 實際畫出來的主機列（把「只看自營」那一檔在前端濾掉；代理商視角沒有這個下拉，一律不濾）。 */
const visibleHosts = computed(() =>
  !auth.isReseller && resellerFilter.value === VENDOR_ONLY ? hosts.value.filter((h) => !h.reseller_id) : hosts.value,
)

/** 歸屬欄的文字：自營／代理商名（名字沒批載回來時退回短 id，不留空白）。 */
function ownerText(h: ManagedHost): string {
  if (!h.reseller_id) return '愛+1 自營'
  return h.reseller_name || h.reseller_id.slice(0, 8)
}

// --- 磁碟掛載點：清單 API 只有「最高水位」，明細要抓詳情 → 停留 300ms 才抓、每台快取一次 ---
const mounts = ref<Record<string, HostMount[]>>({})
const hovered = ref<string | null>(null)
let hoverTimer: number | null = null

function clearHoverTimer() {
  if (hoverTimer !== null) {
    window.clearTimeout(hoverTimer)
    hoverTimer = null
  }
}
/**
 * popup 的**視窗座標**（批121）。
 *
 * ★★ 原本是 `position: absolute` 掛在格子裡——而表格外層 `.tbl-scroll` 是
 *    `overflow-x: auto`，依 CSS 規範另一軸的 `visible` 會被當成 `auto`，於是
 *    **超出表格範圍的部分直接被裁掉**：最後一列的 popup 94px 高有 83px 看不到，
 *    使用者的感受就是「移上去沒反應」（主人 2026-09-21 回報）。
 *    改成 `fixed` ＋ 進入時量一次座標，就不受任何祖先的 overflow 影響。
 * ★ 空間不夠時往上翻（`flip`）：最後一列往下展開會掉出視窗底部。
 */
const popAt = ref<{ left: number; top: number; flip: boolean } | null>(null)
/** popup 的估計高度（量不到實際高度時的保守值，只用來決定要不要往上翻）。 */
const POP_H = 160

function enterDisk(h: ManagedHost, e: MouseEvent) {
  hovered.value = h.id
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
  const below = window.innerHeight - r.bottom
  popAt.value = {
    left: Math.max(8, Math.min(r.left, window.innerWidth - 320)),
    top: below >= POP_H ? r.bottom + 4 : r.top - 4,
    flip: below < POP_H,
  }
  clearHoverTimer()
  if (mounts.value[h.id]) return
  hoverTimer = window.setTimeout(() => {
    hoverTimer = null
    void hosting
      .getManagedHost(h.id)
      .then((d) => {
        mounts.value[h.id] = d.metrics?.mounts ?? []
      })
      .catch(() => {})
  }, 300)
}
function leaveDisk() {
  hovered.value = null
  popAt.value = null
  clearHoverTimer()
}
onUnmounted(clearHoverTimer)

// --- 註冊主機（admin+）---
const createOpen = ref(false)
const busy = ref(false)
const formError = ref('')
const blank = () => ({
  name: '',
  fqdn: '',
  public_ip: '',
  provider: 'gcp',
  region: '',
  exclusive: false,
  exclusive_customer_id: '',
  /** 歸屬代理商（批99-B）；`''`＝我方自營。代理商視角不顯示這一欄（後端強制寫成自己）。 */
  reseller_id: '',
  max_sites: '',
  notes: '',
})
const form = ref(blank())

const NAME_RE = /^[a-z0-9][a-z0-9-]{1,62}$/
const nameOk = computed(() => NAME_RE.test(form.value.name.trim()))

async function openCreate() {
  form.value = blank()
  formError.value = ''
  createOpen.value = true
  try {
    await store.ensureCustomers() // 獨佔客戶下拉用
  } catch {
    /* 拿不到客戶清單不阻斷建立（獨佔可事後在詳情頁指定） */
  }
  // 代理商下拉用（我方視角）。admin 被 403 時 `resellersDenied`＝true，下拉只會有「自營」。
  if (!auth.isReseller) await store.ensureResellers().catch(() => {})
}

// 建立成功後的一次性註冊碼（明碼只有這一次）
const enroll = ref<EnrollInfo | null>(null)
const enrollHost = ref('')
const enrollOpen = ref(false)

async function submitCreate() {
  const f = form.value
  if (!nameOk.value) {
    formError.value = '主機代號只能用小寫英數與連字號、長度 2–63（例：gcp-tw-01）。'
    return
  }
  const body: ManagedHostIn = {
    name: f.name.trim(),
    fqdn: f.fqdn.trim() || null,
    public_ip: f.public_ip.trim() || null,
    provider: f.provider.trim() || null,
    region: f.region.trim() || null,
    exclusive: f.exclusive,
    exclusive_customer_id: f.exclusive && f.exclusive_customer_id ? f.exclusive_customer_id : null,
    max_sites: f.max_sites ? Number(f.max_sites) : null,
    notes: f.notes.trim() || null,
  }
  // 代理商 realm 的 `POST /hosts` 不收 `reseller_id`（後端強制歸自己）——**連送都不送**，
  // 免得看起來像「可以指定別家」。
  if (!auth.isReseller && f.reseller_id) body.reseller_id = f.reseller_id
  busy.value = true
  try {
    const created = await hosting.createManagedHost(body)
    createOpen.value = false
    await poll.refresh()
    // 批102 V4：代理商 realm 的回應**沒有** `enroll`（註冊碼只由我方發），改顯示後端
    // 給的那句下一步提示。
    if (created.enroll) {
      enroll.value = created.enroll
      enrollHost.value = created.name
      enrollOpen.value = true
    } else {
      await dialogs.notify({
        title: '主機已登記',
        message:
          created.enroll_hint ||
          '主機已登記，狀態為「待註冊」。請聯繫愛+1 開通這台主機——我方會提供一次性註冊碼與安裝指令。',
      })
    }
  } catch (e) {
    formError.value = errText(e, '註冊主機失敗')
  } finally {
    busy.value = false
  }
}

async function closeEnroll() {
  enrollOpen.value = false
  enroll.value = null
  await dialogs.notify({
    title: '註冊碼已關閉',
    message: '註冊碼明碼已不可查看。若 VM 上還沒跑安裝指令，請到主機詳情頁「重發註冊碼」。',
  })
}

function sitesText(h: ManagedHost): string {
  return `${h.sites_count} / ${h.max_sites ?? '不限'}`
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>代管主機</h1>
      <span class="count">{{ visibleHosts.length }}</span>
      <span class="spacer" />
      <!-- 歸屬過濾（批99-B）：只有我方視角有意義——代理商看得到的本來就只有自家主機。
           `resellerOptions` 為空（一家代理商都沒有／admin 讀不到清單）時整個下拉不畫。 -->
      <label v-if="!auth.isReseller && (filterOptions.length || resellerFilter)" class="ownerfilter">
        <span class="subtle">歸屬</span>
        <select v-model="resellerFilter">
          <option value="">全部</option>
          <option value="vendor">愛+1 自營</option>
          <option v-for="r in filterOptions" :key="r.id" :value="r.id">{{ r.name }}</option>
        </select>
      </label>
      <label class="retired"><input v-model="includeRetired" type="checkbox" /> 顯示已退役</label>
      <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
      <UiButton v-if="auth.isAdminOrAbove || auth.isReseller" variant="primary" @click="openCreate">＋ 註冊主機</UiButton>
    </div>

    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>

    <div v-else class="card">
      <p v-if="loadError" class="err">{{ loadError }}</p>
      <div v-if="!visibleHosts.length" class="empty">
        <div class="empty-title">尚無代管主機</div>
        <div class="empty-sub">
          {{
            auth.isAdminOrAbove || auth.isReseller
              ? '按右上「＋ 註冊主機」建立，再把安裝指令貼到該台 VM 執行。'
              : '請管理員先註冊主機。'
          }}
        </div>
      </div>

      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>主機</th>
              <!-- 歸屬（批99-B）：代理商視角不畫（他的主機恆是自己的） -->
              <th v-if="!auth.isReseller">歸屬</th>
              <th>狀態</th>
              <th class="res-h">資源水位</th>
              <th>站數</th>
              <!-- 批141：代管種類（agent 種類；註冊時定下；門檻與安裝方式在〈產品 › 代管種類〉）。 -->
              <th>代管種類</th>
              <th>agent</th>
              <th>最後回報</th>
              <th>告警</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="h in visibleHosts" :key="h.id">
              <td>
                <div class="hostcell">
                  <RouterLink class="hname" :to="{ name: hr('host-detail'), params: { id: h.id } }">{{ h.name }}</RouterLink>
                  <span class="hsub">
                    {{ h.public_ip || h.fqdn || '未填 IP' }}
                    <template v-if="h.provider || h.region">· {{ [h.provider, h.region].filter(Boolean).join(' ') }}</template>
                  </span>
                  <span v-if="h.exclusive" class="chip excl">
                    獨佔{{ h.exclusive_customer_name ? `：${h.exclusive_customer_name}` : '（未指定客戶）' }}
                  </span>
                </div>
              </td>
              <td v-if="!auth.isReseller" class="nowrap">
                <span v-if="h.reseller_id" class="chip owner">{{ ownerText(h) }}</span>
                <span v-else class="subtle">{{ ownerText(h) }}</span>
              </td>
              <td>
                <span class="status-pill" :class="hostStatusClass(h.status)">{{ hostStatusLabel(h.status) }}</span>
              </td>
              <td class="res">
                <UiMeter label="CPU" :pct="h.cpu_pct" />
                <UiMeter label="RAM" :pct="h.mem_used_pct" />
                <div class="disk-wrap" @mouseenter="enterDisk(h, $event)" @mouseleave="leaveDisk">
                  <UiMeter label="磁碟" :pct="h.disk_used_pct_max" title="各掛載點的最高水位（停留可看全部掛載點）" />
                  <!-- ★ `Teleport` 到 body：留在格子裡會被 `.tbl-scroll` 的 overflow 裁掉（見 `enterDisk`）。 -->
                  <Teleport v-if="hovered === h.id && popAt" to="body">
                  <div
                    class="mpop"
                    :style="{
                      left: `${popAt.left}px`,
                      top: `${popAt.top}px`,
                      transform: popAt.flip ? 'translateY(-100%)' : undefined,
                    }"
                  >
                    <div v-if="!mounts[h.id]" class="subtle">讀取掛載點…</div>
                    <template v-else-if="mounts[h.id].length">
                      <div v-for="m in mounts[h.id]" :key="mountPath(m)" class="mrow">
                        <code class="mono">{{ mountPath(m) }}</code>
                        <UiMeter :pct="m.used_pct" />
                        <span class="msize">
                          {{ fmtCapacity(m.used_mb, m.used_bytes) }} / {{ fmtCapacity(m.total_mb, m.total_bytes) }}
                        </span>
                      </div>
                    </template>
                    <div v-else class="subtle">agent 尚未回報掛載點。</div>
                  </div>
                  </Teleport>
                </div>
              </td>
              <td class="nowrap">{{ sitesText(h) }}</td>
              <td class="nowrap"><code class="mono">{{ h.agent_kind || '—' }}</code></td>
              <td>
                <div class="agentcell">
                  <span class="nowrap">
                    <template v-if="h.agent_enrolled">{{ h.agent_version || '未回報版本' }}</template>
                    <span v-else class="status-pill pending">未註冊</span>
                  </span>
                  <!-- 支援產品（批99-A）：agent 自報「這台裝得起哪些產品」。
                       ★ 批114（裁示 ㉟）：舊 agent 的兜底規則拿掉了——**null＝尚未回報**，
                         這台開不了站，所以直接指路去升 agent，不替它假設成某個產品。 -->
                  <span class="chips prodchips">
                    <span v-for="k in h.agent_products ?? []" :key="k" class="chip">{{ store.productName(k) }}</span>
                    <span
                      v-if="h.agent_products == null"
                      class="warn-note"
                      title="agent 0.2.11 以前不會回報支援產品。中台不再替它假設，這台在升級 agent 之前無法開站。"
                    >
                      <span class="wn-long">agent 尚未回報支援的產品，請升級 agent</span>
                      <!-- 批171：手機／平板放不下整句（每列被撐到 200px 高）→ 短版，完整說明在 title。 -->
                      <span class="wn-short">⚠ 請升級 agent</span>
                    </span>
                    <span v-else-if="!h.agent_products.length" class="subtle">
                      回報「目前一個產品都裝不起來」
                    </span>
                  </span>
                </div>
              </td>
              <td class="seen" :class="agentStaleLevel(h.last_seen)">{{ fmtSeen(h.last_seen) }}</td>
              <td>
                <RouterLink
                  v-if="h.alert_counts.crit || h.alert_counts.warn || h.alert_counts.info"
                  class="abadges"
                  :to="{ name: hr('alerts') }"
                  title="到告警頁查看"
                >
                  <span v-if="h.alert_counts.crit" class="badge">{{ h.alert_counts.crit }}</span>
                  <span v-if="h.alert_counts.warn" class="badge warn">{{ h.alert_counts.warn }}</span>
                  <span v-if="h.alert_counts.info" class="badge info">{{ h.alert_counts.info }}</span>
                </RouterLink>
                <span v-else class="subtle">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 註冊主機：建立即發第一張一次性註冊碼 -->
    <UiModal :open="createOpen" title="註冊代管主機" width="620px" @close="createOpen = false">
      <div class="form-grid">
        <label class="field">
          <span>主機代號 *</span>
          <input v-model="form.name" placeholder="gcp-tw-01（小寫英數與 -，建立後不可改）" />
        </label>
        <div class="field-row two">
          <label class="field">
            <span>管理用 FQDN</span>
            <input v-model="form.fqdn" placeholder="例：host-1.example.com" />
          </label>
          <label class="field">
            <span>對外 IP</span>
            <input v-model="form.public_ip" placeholder="34.80.101.55（可留空，agent 註冊時自動補）" />
          </label>
        </div>
        <div class="field-row two">
          <label class="field"><span>供應商</span><input v-model="form.provider" placeholder="gcp" /></label>
          <label class="field"><span>區域</span><input v-model="form.region" placeholder="asia-east1" /></label>
        </div>
        <!-- 歸屬（批99-B，我方視角專用）：留空＝我方自營。代理商建的主機一律歸自己，
             所以他的表單上沒有這一欄（後端也不收這個欄位）。 -->
        <label v-if="!auth.isReseller && resellerOptions.length" class="field">
          <span>代理商</span>
          <select v-model="form.reseller_id">
            <option value="">愛+1 自營（不掛代理商）</option>
            <option v-for="r in resellerOptions" :key="r.id" :value="r.id">{{ r.name }}</option>
          </select>
        </label>
        <div class="field-row two">
          <label class="field">
            <span>站數上限</span>
            <input v-model="form.max_sites" type="number" min="1" placeholder="留空＝只看資源" />
          </label>
          <label class="field excl-field">
            <span>獨佔</span>
            <div class="excl-row">
              <label class="cb"><input v-model="form.exclusive" type="checkbox" /> 只給單一客戶</label>
              <select v-model="form.exclusive_customer_id" :disabled="!form.exclusive">
                <option value="">首個開站客戶自動填入</option>
                <option v-for="c in store.customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c, { ownership: !auth.isReseller }) }}</option>
              </select>
            </div>
          </label>
        </div>
        <label class="field">
          <span>備註</span>
          <textarea v-model="form.notes" rows="2" placeholder="規格、用途、注意事項" />
        </label>
        <p v-if="auth.isReseller" class="subtle">
          建立後狀態為「待註冊」。<strong>註冊碼由愛+1 提供</strong>——登記完成後請聯繫我方開通這台主機，
          我方會給您一次性註冊碼與安裝指令；到該台 VM 執行後 agent 才會開始回報。
        </p>
        <p v-else class="subtle">
          建立後狀態為「待註冊」，畫面會給一次性註冊碼；到該台 VM 執行安裝指令、agent 完成 enroll 後才會開始回報。
        </p>
        <p v-if="formError" class="err">{{ formError }}</p>
      </div>
      <template #foot>
        <UiButton @click="createOpen = false">取消</UiButton>
        <UiButton variant="primary" :loading="busy" :disabled="!nameOk" @click="submitCreate">
          {{ auth.isReseller ? '登記主機' : '建立並產生註冊碼' }}
        </UiButton>
      </template>
    </UiModal>

    <!-- 一次性註冊碼（明碼只出現這一次） -->
    <UiModal :open="enrollOpen" title="主機註冊碼（只顯示一次）" width="640px" @close="closeEnroll">
      <EnrollCodeCard v-if="enroll" :enroll="enroll" :host-name="enrollHost" />
      <template #foot>
        <UiButton variant="primary" @click="closeEnroll">我已複製，關閉</UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.retired {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
.err {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
.hostcell {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-start;
}
.hname {
  color: var(--brand-700);
  font-weight: 600;
  font-size: var(--fs-13);
  text-decoration: none;
}
[data-theme='dark'] .hname {
  color: var(--brand-400);
}
.hname:hover {
  text-decoration: underline;
}
.hsub {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
/* agent 欄：版本一行、支援產品 chip 一行（批99-A）。chip 版面沿用 ui.css 的 `.chips`。 */
.agentcell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.prodchips {
  align-items: center;
  font-size: var(--fs-11);
}
/* 批114：agent 尚未回報支援產品（這台開不了站，要去升 agent）。 */
.warn-note {
  color: var(--warning);
  font-weight: 600;
  cursor: help;
}
.warn-note .wn-short {
  display: none;
}
@media (--compact) {
  .warn-note .wn-long {
    display: none;
  }
  .warn-note .wn-short {
    display: inline;
    white-space: nowrap;
  }
  td.seen {
    white-space: nowrap;
  }
}
/* 歸屬欄的代理商膠囊（批99-B）；自營是灰字不加膠囊——多數列是自營，全上色等於沒上色。 */
.chip.owner {
  background: var(--brand-100);
  border-color: transparent;
  color: var(--brand-700);
  font-weight: 600;
}
[data-theme='dark'] .chip.owner {
  background: var(--bg-active);
  color: var(--brand-400);
}
.ownerfilter {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.ownerfilter select {
  height: var(--control-h);
}
.chip.excl {
  background: var(--st-transferred-bg);
  border-color: transparent;
  color: var(--st-transferred-fg);
  font-weight: 600;
}
.res-h {
  min-width: 200px;
}
.res {
  min-width: 200px;
}
.res > * + * {
  margin-top: 3px;
}
.disk-wrap {
  position: relative;
}
/* ★ 批121：`fixed` ＋ Teleport 到 body——`absolute` 會被 `.tbl-scroll` 的 overflow 裁掉
   （最後一列幾乎整個看不見）。座標由 `enterDisk` 量好寫進 inline style。
   z-index 要壓過表格與 sticky 表頭，但**低於** modal／dialog（1050 起）。 */
.mpop {
  position: fixed;
  z-index: 900;
  /* 純資訊卡、裡面沒有可點的東西：讓滑鼠穿透，免得游標滑過它時
     `.disk-wrap` 收到 mouseleave 而閃爍。 */
  pointer-events: none;
  min-width: 300px;
  padding: var(--sp-3);
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  box-shadow: var(--shadow-md);
}
.mrow {
  display: grid;
  grid-template-columns: minmax(70px, auto) 1fr auto;
  align-items: center;
  gap: var(--sp-2);
}
.mrow + .mrow {
  margin-top: 5px;
}
.msize {
  font-size: var(--fs-11);
  color: var(--fg-4);
  white-space: nowrap;
}
.nowrap {
  white-space: nowrap;
}
.abadges {
  display: flex;
  gap: 4px;
  text-decoration: none;
}
.abadges .badge {
  margin-left: 0;
}
.field-row.two {
  display: grid;
  /* 批171：`1fr` 是 `minmax(auto, 1fr)`——右格的最小寬度一大（獨佔那列的客戶下拉＝最長的客戶名），
     左格就被壓到一兩個字寬（「站／數／上／限」直排、數字框約 25px）、整排還超出對話框右緣。 */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--sp-3);
}
.excl-row {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
}
.excl-row select {
  /* 批171：原生 select 的最小寬度＝最長的選項（客戶名），`flex: 1` 仍會把它算進整列的最小寬度
     → 基準寬 0、靠 flex-grow 撐滿，最小寬度就不再被選項長度綁住。 */
  flex: 1 1 0;
  width: 0;
  min-width: 0;
}
.cb {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--fs-12);
  color: var(--fg-2);
  white-space: nowrap;
}
</style>
